import { useMsal } from "@azure/msal-react";
import { ChevronLeft } from "@untitledui/icons";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { ROLE_NAME_MAP } from "@/shared/utils/maps";
import { selectSelectedRole } from "@/store/userSlice";
import { widgetComponentMap, widgetKeyToRoute } from "@/types/index";

const routeToWidgetKey = Object.fromEntries(
  Object.entries(widgetKeyToRoute).map(([key, value]) => [value, key])
);

const MobileWidgetPage = () => {
  const navigate = useNavigate();
  const { account } = useAuth();
  const { widgetKey } = useParams();
  const { accounts } = useMsal();
  const { t } = useTranslation();
  const selectedRole = useSelector(selectSelectedRole);

  const upn =
    (accounts[0]?.idTokenClaims as any)?.preferred_username || accounts[0]?.username || "";

  const displayName = accounts[0]?.name || account?.displayName || "Guest";

  const resolvedComponentKey = routeToWidgetKey[widgetKey || ""];
  const Component = widgetComponentMap[resolvedComponentKey];

  const widgetTitle: Record<string, string> = {
  "quick-access": t("components.widgets.quickAccessApps.title"),
  "active-programs": t("components.activePrograms.title"),
  "task-priorities": t("components.taskPriorities.title"),
  notifications: t("components.notificationTitle"),
  guidelines: t("components.keyGuidelines.title"),
  meetings: t("components.MeetingsWidget.title"),
  "impact-targets": t("components.widgets.financeTargets.title"),
  "team-calendar": t("widgetSidebarConfig.TeamCalendar"),
  partners: t("components.partnerDirectory.title"),
  learning: t("widgetSidebarConfig.LearningInsights"),
  analytics: t("components.analyticsInsights.financialAnalytics"),
  resources: t("components.widgets.programResources.title"),
  oversight: t("components.OversightWidget.title"),
  submissions: t("components.mySubmission.title"),
  partnersubmissions: t("components.partnerSubmission.title"),
  disbursements: t("components.disbursementRequestsWrapper.title"),

    "world-clocks": t("widgetSidebarConfig.WorldClocks"),
    "account-settings": t("widgetSidebarConfig.AccountSettings"),
    accessibility: t("widgetSidebarConfig.Accessibility"),
    terms: t("widgetSidebarConfig.Terms"),
    "ideas-tracker": t("components.widgets.ideasTracker.title"),
    "program-impact": t("components.widgets.programImpact.title"),
    "pipeline-priority" : t("components.PipelinePriority.title")
  };
  const subtitleMap: Record<string, string> = {
    "quick-access": t("widgetSidebarWidgetsSubtitles.QuickAccessApps"),
    "active-programs": t("widgetSidebarWidgetsSubtitles.ActivePrograms"),
    "task-priorities": t("widgetSidebarWidgetsSubtitles.TaskPriorities"),
    notifications: t("widgetSidebarWidgetsSubtitles.Notifications"),
    guidelines: t("widgetSidebarWidgetsSubtitles.KeyGuidelines"),
    meetings: t("widgetSidebarWidgetsSubtitles.UpcomingMeetings"),
    "impact-targets": t("widgetSidebarWidgetsSubtitles.FinanceTargets"),
    "team-calendar": t("widgetSidebarWidgetsSubtitles.TeamCalendar"),
    partners: t("widgetSidebarWidgetsSubtitles.PartnersDirectory"),
    learning: t("widgetSidebarWidgetsSubtitles.LearningInsights"),
    analytics: t("widgetSidebarWidgetsSubtitles.FinancialAnalytics"),
    resources: t("widgetSidebarWidgetsSubtitles.ProgramResources"),
    oversight: t("widgetSidebarWidgetsSubtitles.Oversight"),
    submissions: t("widgetSidebarWidgetsSubtitles.MySubmissions"),
    disbursements: t("widgetSidebarWidgetsSubtitles.DisbursementRequests"),
    "world-clocks": t("widgetSidebarWidgetsSubtitles.WorldClocks"),
    "account-settings": t("widgetSidebarWidgetsSubtitles.AccountSettings"),
    accessibility: t("widgetSidebarWidgetsSubtitles.Accessibility"),
  };
  return (
    <div className="min-h-[100dvh] bg-[var(--bg-main)] text-[var(--text-color)]">
      <div className="flex items-center gap-3 px-4 py-3 bg-[var(--panel-bg)] border-b border-[var(--border-color)]">
        <button onClick={() => navigate(-1)}>
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div>
          <h1 className="text-lg font-semibold">{widgetTitle[widgetKey || ""]}</h1>
          <p className="text-xs text-[var(--text-secondary)]">{subtitleMap[widgetKey || ""]}</p>
        </div>
      </div>

      <div className="pb-6 flex flex-col min-h-0">
        <Suspense fallback={<div className="p-4 text-[var(--text-secondary)]">Loading...</div>}>
          {Component ? (
            <Component
              open={true}
              upn={upn}
              profile={{
                displayName,
                email: upn,
                organization: "Mastercard Foundation",
                role: ROLE_NAME_MAP[selectedRole] || selectedRole,
              }}
              onClose={() => navigate(-1)}
            />
          ) : (
            <div className="p-4 text-[var(--text-color)]">Coming soon</div>
          )}
        </Suspense>
      </div>
    </div>
  );
};

export default MobileWidgetPage;
``;
